import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

/**
 * The waiting state, drawn rather than spun.
 *
 * A spinner says "something is happening" and nothing else. This draws one
 * line-icon on, holds it, erases it, and moves to the next — the column from
 * the mark, a house, a key. It reads as deliberate rather than stalled, and
 * costs nothing: no images, no animation library, three CSS keyframes.
 *
 * How it works: the dash is made longer than the whole path, so the visible
 * stroke is one continuous segment, and sliding stroke-dashoffset from +LEN to
 * -LEN moves that segment across the path and off the far end. Draw and erase
 * out of a single number.
 */

// Drawn inside an 80x80 box, one `d` each. Extra M commands just lift the pen.
const GLYPHS = [
  // the column from the mark
  "M14 22 H66 M20 30 H60 M28 30 V62 M40 30 V62 M52 30 V62 M18 62 H62",
  // a house
  "M16 40 L40 20 L64 40 M22 36 V62 H58 V36 M34 62 V48 H46 V62",
  // a key, for handover
  "M26 30 a11 11 0 1 0 0 22 a11 11 0 1 0 0 -22 M37 41 H64 M56 41 V50 M62 41 V48",
];

// Must exceed the longest glyph, or the stroke leaves a stub that never
// erases. The longest here measures about 232.
const PATH_LEN = 320;
const CYCLE_MS = 2600;

const LoadingScreen = ({ message = "Loading" }) => {
  const [index, setIndex] = useState(0);
  const reduced = useRef(false);

  useEffect(() => {
    reduced.current =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    // Somebody who has asked for less motion keeps a still, finished icon.
    if (reduced.current) return undefined;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % GLYPHS.length),
      CYCLE_MS
    );
    return () => clearInterval(id);
  }, []);

  return (
    <Centre role="status" aria-live="polite">
      <svg width="88" height="88" viewBox="0 0 80 80" aria-hidden="true">
        {/* key remounts the path each swap so the keyframes restart cleanly */}
        <path
          key={index}
          className="glyph"
          d={GLYPHS[index]}
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={PATH_LEN}
        />
      </svg>
      <span className="visually-hidden">{message}</span>
    </Centre>
  );
};

export default LoadingScreen;

const Centre = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;

  .glyph {
    stroke: var(--accent);
    stroke-dashoffset: ${PATH_LEN};
    animation: draw-glyph ${CYCLE_MS}ms ease-in-out forwards;
  }

  @keyframes draw-glyph {
    0% {
      stroke-dashoffset: ${PATH_LEN};
    }
    40% {
      stroke-dashoffset: 0;
    }
    60% {
      stroke-dashoffset: 0;
    }
    100% {
      stroke-dashoffset: ${-PATH_LEN};
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .glyph {
      animation: none;
      stroke-dashoffset: 0;
    }
  }
`;
